/**
 * SSO 교환 실패 진단 — web-app·quiz-app 공용 단일 출처.
 *
 * 다이얼로그 UI 는 앱마다 디자인 시스템이 달라 각자 구현하지만(web-app=overlay-kit,
 * quiz-app=alertDialog), **어떤 신호를 수집해 어떤 태그로 보낼지는 공용**이어야 한다.
 * web-app 에만 있던 탓에 실패 건수가 13배 많은 quiz-app 이 진단 없이 방치됐다.
 */

export interface SsoCookieDiagnostics {
  /** navigator.cookieEnabled — 전체 쿠키 차단 여부 */
  cookieEnabled: boolean;
  /** iframe(third-party) 컨텍스트 여부. cross-origin top 접근 throw 시 true 로 간주 */
  framed: boolean;
  /** 이 페이지로 들어온 referrer host (임베드 출처 파악용) */
  referrerHost: string;
  /** .zep.us 에 SameSite=None;Secure 쿠키가 실제로 저장되는지 프로브 결과 */
  probeCookieStored: boolean;
  /** 교환 토큰 쿠키(ZEP_EXCHANGE_TOKEN, non-HttpOnly)가 브라우저에 있는지 */
  hasExchangeTokenCookie: boolean;
  /** JS 가시 쿠키 개수 (HttpOnly 제외) */
  visibleCookieCount: number;
}

/**
 * SSO 로그인 실패 원인 진단용 클라 신호 수집 (Sentry captureError extra 용).
 *
 * 로그인 200 직후에도 세션/교환 쿠키가 브라우저에 안 붙어 401 로 튕기는 케이스의
 * 원인 후보 — iframe/third-party 컨텍스트 · 전체 쿠키 차단 — 는 서버 로그(Referer/
 * Sec-Fetch 미기록)로 판별 불가라 실패 시점에 클라에서 직접 수집한다.
 * 진단 전용이므로 어떤 접근도 throw 하지 않는다(동작 무변경).
 */
export function collectSsoCookieDiagnostics(): SsoCookieDiagnostics {
  const diag: SsoCookieDiagnostics = {
    cookieEnabled: false,
    framed: false,
    referrerHost: '',
    probeCookieStored: false,
    hasExchangeTokenCookie: false,
    visibleCookieCount: 0,
  };
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return diag;
  }
  try {
    diag.cookieEnabled = navigator.cookieEnabled;
  } catch {
    /* noop */
  }
  try {
    diag.framed = window.self !== window.top;
  } catch {
    // cross-origin 이면 top 접근 자체가 throw → third-party iframe 로 간주
    diag.framed = true;
  }
  try {
    diag.referrerHost = document.referrer
      ? new URL(document.referrer).host
      : '';
  } catch {
    /* noop */
  }
  try {
    const cookies = document.cookie ? document.cookie.split('; ') : [];
    diag.visibleCookieCount = cookies.length;
    diag.hasExchangeTokenCookie = cookies.some(c =>
      c.startsWith('ZEP_EXCHANGE_TOKEN='),
    );
  } catch {
    /* noop */
  }
  try {
    document.cookie =
      'zep_sso_probe=1; Domain=.zep.us; Secure; SameSite=None; Max-Age=60; Path=/';
    diag.probeCookieStored = /(?:^|; )zep_sso_probe=1(?:;|$)/.test(
      document.cookie,
    );
    document.cookie = 'zep_sso_probe=; Domain=.zep.us; Path=/; Max-Age=0';
  } catch {
    /* noop */
  }
  return diag;
}

/**
 * axios 계열 에러에서 HTTP status 를 안전하게 추출 (ZepApiProvider 가 심는
 * httpStatus, 없으면 response.status). 진단 로깅용.
 */
export function getErrorHttpStatus(error: unknown): number | undefined {
  if (error && typeof error === 'object') {
    const e = error as {
      httpStatus?: unknown;
      response?: { status?: unknown };
    };
    if (typeof e.httpStatus === 'number') return e.httpStatus;
    if (
      e.response &&
      typeof e.response === 'object' &&
      typeof e.response.status === 'number'
    ) {
      return e.response.status;
    }
  }
  return undefined;
}

/**
 * Sentry beforeSend 가 이 값을 보고 SSO 진단 이벤트를 노이즈 필터에서 살려둔다.
 * ⚠️ @zep/sentry 의 SSO_COOKIE_DIAGNOSTIC_TAG 와 **같은 문자열이어야** 한다
 * (그 패키지는 dist 로 발행돼 여기서 import 할 수 없다).
 */
export const SSO_DIAGNOSTIC_TAG = 'sso_cookie';

/** Sentry captureError 에 실을 태그/extra. 두 앱이 같은 키로 보내야 한 쿼리로 읽힌다. */
export function buildSsoErrorReport(
  error: unknown,
  options: { tokenSource: 'url' | 'cookie' },
) {
  const diagnostics = collectSsoCookieDiagnostics();
  const httpStatus = getErrorHttpStatus(error);
  return {
    tags: {
      feature: 'sso',
      diagnostic: SSO_DIAGNOSTIC_TAG,
      action: 'error_dialog',
      framed: String(diagnostics.framed),
      cookie_enabled: String(diagnostics.cookieEnabled),
      probe_cookie_stored: String(diagnostics.probeCookieStored),
      has_exchange_token_cookie: String(diagnostics.hasExchangeTokenCookie),
      token_source: options.tokenSource,
      ...(httpStatus != null ? { http_status: String(httpStatus) } : {}),
    },
    extra: { ...diagnostics, httpStatus, tokenSource: options.tokenSource },
  };
}

/**
 * 분석 이벤트(track)용 판정 필드. Sentry `extra` 는 스크러빙에 걸려 읽을 수 없어
 * (`referrerHost: "[Filtered]"` 실측) 같은 신호를 ClickHouse 로도 보낸다.
 *
 * 401 이 "로그인 직후인데 토큰이 없음"인지 "로그인을 안 거치고 이 페이지에 도달"인지
 * 가르는 게 목적이다. 실측상 94%가 페이지 도착 5초 내 실패라 TTL 만료는 아니다.
 */
export interface SsoErrorDiagnosticFields {
  /** 직전 페이지 host. 로그인 리다이렉트면 auth 호스트, 북마크·탭복원이면 빈 값. */
  referrer_host: string;
  /** auth-app 이 붙인 from_login=1 여부. referrer 정책에 안 흔들리는 판정값. */
  from_login: boolean;
  /** .zep.us SameSite=None 쿠키가 저장되는 브라우저인지(probe). */
  probe_cookie_stored: boolean;
  /** 교환용 쿠키(ZEP_EXCHANGE_TOKEN)가 브라우저에 있었는지. */
  has_exchange_cookie: boolean;
  /** JS 가 읽을 수 있는 쿠키 수. jar eviction 신호. */
  visible_cookie_count: number;
}

export function buildSsoErrorTrackFields(
  fromLogin: boolean,
): SsoErrorDiagnosticFields {
  const d = collectSsoCookieDiagnostics();
  return {
    // 로그인 리다이렉트면 auth 호스트, 북마크·탭복원이면 빈 값
    referrer_host: d.referrerHost,
    // auth-app getServiceRedirectUrl 이 붙이는 from_login=1 — referrer 정책에 안 흔들린다
    from_login: fromLogin,
    probe_cookie_stored: d.probeCookieStored,
    has_exchange_cookie: d.hasExchangeTokenCookie,
    visible_cookie_count: d.visibleCookieCount,
  };
}
